/**
 * Main St. Gifts — Dynamic Pricing Engine v4
 * =============================================
 * INSTALL:
 * 1. Shopify Admin → Online Store → Themes → Edit Code
 * 2. Assets → Add new asset → blank file → mainst-pricing-engine.js → paste this → Save
 * 3. Open sections/main-product.liquid → scroll to bottom → add:
 *    <script src="{{ 'mainst-pricing-engine.js' | asset_url }}" defer></script>
 *
 * WHAT THIS DOES:
 * - Injects a smart quantity input — customer types how many they want
 * - Script auto-selects the correct Shopify variant (qty range) silently
 * - Counts selected imprint locations to determine location upcharge
 * - Calculates exact per-unit price × actual qty entered
 * - Shows live price breakdown above Add to Cart
 * - Triggers quote prompt for embroidery, 5+ colors, 504+ qty, blankets
 *
 * GLOBO OPTION SET REQUIRED (apparel):
 * 1. Decoration Type    — radio button
 * 2. Number of Colors   — dropdown (2 colors, 3 colors, 4 colors, 5+ colors)
 * 3. Imprint Location   — dropdown, multi-select, min 1 max 3
 * 4. Color Match        — checkbox
 * 5. Design File Upload — file upload
 *
 * SHOPIFY VARIANTS REQUIRED:
 * Option 1: Quantity Range (QTY 1-23, QTY 24-47, ... QTY 504+)
 * Option 2: Garment Color
 * Tag each product: pricing:apparel, pricing:drinkware, etc.
 */

// ── GLOBO FIELD LABELS — must match your Globo option names exactly ────────────
const GLOBO_FIELD_LABELS = {
  numColors:       'Number of Colors',
  decorationType:  'Decoration Type',
  imprintLocation: 'Imprint Location',
  hatPatch:        'Hat Patch',
  goldRim:         'Gold Rim',
  laserEtchSide:   'Laser Etch Side',
  laserEtchInside: 'Laser Etch Inside',
  preciousMetal:   'Precious Metal',
  colorMatch:      'Color Match',
};

// ── QTY RANGE DEFINITIONS ─────────────────────────────────────────────────────
// Maps a number entered by the customer to the correct Shopify variant label
// and break index for upcharge lookups.
// Format: [minQty, maxQty, variantLabel, breakIndex]
// maxQty of Infinity = open-ended top range
// breakIndex of 'quote' = triggers quote prompt

const QTY_RANGES = {
  apparel: [
    [1,   23,  'QTY 1-23',    0],
    [24,  47,  'QTY 24-47',   1],
    [48,  71,  'QTY 48-71',   2],
    [72,  99,  'QTY 72-99',   3],
    [100, 287, 'QTY 100-287', 4],
    [288, 503, 'QTY 288-503', 5],
    [504, Infinity, 'QTY 504+', 'quote'],
  ],
  drinkware: [
    [1,   23,   'QTY 1-23',    'quote'],
    [24,  47,   'QTY 24-47',   0],
    [48,  71,   'QTY 48-71',   1],
    [72,  99,   'QTY 72-99',   2],
    [100, 287,  'QTY 100-287', 3],
    [288, 503,  'QTY 288-503', 4],
    [504, Infinity, 'QTY 504+', 5],
  ],
  tumblers: [
    [1,    35,   'QTY 1-35',    'quote'],
    [36,   71,   'QTY 36-71',   0],
    [72,   143,  'QTY 72-143',  1],
    [144,  287,  'QTY 144-287', 2],
    [288,  503,  'QTY 288-503', 3],
    [504,  1007, 'QTY 504-1007',4],
    [1008, Infinity, 'QTY 1008+', 5],
  ],
  'travel-mugs': [
    [1,   23,   'QTY 1-23',    'quote'],
    [24,  47,   'QTY 24-47',   0],
    [48,  71,   'QTY 48-71',   1],
    [72,  143,  'QTY 72-143',  2],
    [144, 287,  'QTY 144-287', 3],
    [288, 503,  'QTY 288-503', 4],
    [504, Infinity, 'QTY 504+', 5],
  ],
  'water-bottles': [
    [1,   23,   'QTY 1-23',    'quote'],
    [24,  47,   'QTY 24-47',   0],
    [48,  71,   'QTY 48-71',   1],
    [72,  143,  'QTY 72-143',  2],
    [144, 287,  'QTY 144-287', 3],
    [288, 503,  'QTY 288-503', 4],
    [504, Infinity, 'QTY 504+', 5],
  ],
  'writing-instruments': [
    [1,    249,  'QTY 1-249',    'quote'],
    [250,  499,  'QTY 250-499',  0],
    [500,  999,  'QTY 500-999',  1],
    [1000, 1499, 'QTY 1000-1499',2],
    [1500, 1999, 'QTY 1500-1999',3],
    [2000, 2499, 'QTY 2000-2499',4],
    [2500, Infinity, 'QTY 2500+', 5],
  ],
  lanyards: [
    [1,    99,   'QTY 1-99',     'quote'],
    [100,  249,  'QTY 100-249',  0],
    [250,  499,  'QTY 250-499',  1],
    [500,  999,  'QTY 500-999',  2],
    [1000, 2499, 'QTY 1000-2499',3],
    [2500, 4999, 'QTY 2500-4999',4],
    [5000, Infinity, 'QTY 5000+', 5],
  ],
  'stress-relievers': [
    [1,    149,  'QTY 1-149',    'quote'],
    [150,  249,  'QTY 150-249',  0],
    [250,  499,  'QTY 250-499',  1],
    [500,  999,  'QTY 500-999',  2],
    [1000, 2499, 'QTY 1000-2499',3],
    [2500, 4999, 'QTY 2500-4999',4],
    [5000, Infinity, 'QTY 5000+', 5],
  ],
  magnets: [
    [1,    49,   'QTY 1-49',    'quote'],
    [50,   99,   'QTY 50-99',   0],
    [100,  249,  'QTY 100-249', 1],
    [250,  499,  'QTY 250-499', 2],
    [500,  999,  'QTY 500-999', 3],
    [1000, 1499, 'QTY 1000-1499',4],
    [1500, Infinity, 'QTY 1500+', 5],
  ],
  'bottle-openers': [
    [1,    249,  'QTY 1-249',    'quote'],
    [250,  499,  'QTY 250-499',  0],
    [500,  999,  'QTY 500-999',  1],
    [1000, 2499, 'QTY 1000-2499',2],
    [2500, 4999, 'QTY 2500-4999',3],
    [5000, Infinity, 'QTY 5000+', 4],
  ],
  'ice-scrapers': [
    [1,    149,  'QTY 1-149',   'quote'],
    [150,  249,  'QTY 150-249', 0],
    [250,  499,  'QTY 250-499', 1],
    [500,  999,  'QTY 500-999', 2],
    [1000, 1499, 'QTY 1000-1499',3],
    [1500, Infinity, 'QTY 1500+', 4],
  ],
  sunglasses: [
    [1,    149,  'QTY 1-149',   'quote'],
    [150,  249,  'QTY 150-249', 0],
    [250,  499,  'QTY 250-499', 1],
    [500,  999,  'QTY 500-999', 2],
    [1000, 2499, 'QTY 1000-2499',3],
    [2500, Infinity, 'QTY 2500+', 4],
  ],
  'flying-disks': [
    [1,    99,   'QTY 1-99',    'quote'],
    [100,  249,  'QTY 100-249', 0],
    [250,  499,  'QTY 250-499', 1],
    [500,  999,  'QTY 500-999', 2],
    [1000, 2499, 'QTY 1000-2499',3],
    [2500, Infinity, 'QTY 2500+', 4],
  ],
  'beach-towels': [
    [1,   23,   'QTY 1-23',    'quote'],
    [24,  71,   'QTY 24-71',   0],
    [72,  143,  'QTY 72-143',  1],
    [144, 299,  'QTY 144-299', 2],
    [300, 499,  'QTY 300-499', 3],
    [500, Infinity, 'QTY 500+', 4],
  ],
  blankets: [
    [1, Infinity, 'QTY 1+', 'quote'], // always quote
  ],
  golf: [
    [1,   5,   'QTY 1-5',    'quote'],
    [6,   11,  'QTY 6-11',   0],
    [12,  23,  'QTY 12-23',  1],
    [24,  47,  'QTY 24-47',  2],
    [48,  119, 'QTY 48-119', 3],
    [120, 499, 'QTY 120-499',4],
    [500, Infinity, 'QTY 500+', 5],
  ],
  'can-coolers': [
    [1,   49,  'QTY 1-49',   'quote'],
    [50,  99,  'QTY 50-99',  0],
    [100, 249, 'QTY 100-249',1],
    [250, 499, 'QTY 250-499',2],
    [500, 999, 'QTY 500-999',3],
    [1000, Infinity, 'QTY 1000+', 4],
  ],
  flashlights: [
    [1,    99,   'QTY 1-99',     'quote'],
    [100,  249,  'QTY 100-249',  0],
    [250,  499,  'QTY 250-499',  1],
    [500,  999,  'QTY 500-999',  2],
    [1000, 2499, 'QTY 1000-2499',3],
    [2500, Infinity, 'QTY 2500+', 4],
  ],
  'stuffed-animals': [
    [1,    49,  'QTY 1-49',   'quote'],
    [50,   99,  'QTY 50-99',  0],
    [100,  249, 'QTY 100-249',1],
    [250,  499, 'QTY 250-499',2],
    [500,  999, 'QTY 500-999',3],
    [1000, Infinity, 'QTY 1000+', 4],
  ],
  buttons: [
    [1,     49,    'QTY 1-49',     'quote'],
    [50,    99,    'QTY 50-99',    0],
    [100,   249,   'QTY 100-249',  1],
    [250,   499,   'QTY 250-499',  2],
    [500,   999,   'QTY 500-999',  3],
    [1000,  2499,  'QTY 1000-2499',4],
    [2500,  4999,  'QTY 2500-4999',5],
    [5000,  9999,  'QTY 5000-9999',6],
    [10000, Infinity, 'QTY 10000+', 7],
  ],
  candles: [
    [1,    23,   'QTY 1-23',    'quote'],
    [24,   71,   'QTY 24-71',   0],
    [72,   143,  'QTY 72-143',  1],
    [144,  287,  'QTY 144-287', 2],
    [288,  503,  'QTY 288-503', 3],
    [504,  1007, 'QTY 504-1007',4],
    [1008, Infinity, 'QTY 1008+', 5],
  ],
};

// ── COLOR UPCHARGE TABLES ─────────────────────────────────────────────────────
// Structure: COLOR_UPCHARGES[productType][colorCount][breakIndex]

const COLOR_UPCHARGES = {
  apparel: {
    //     R1(1-23) R2(24-47) R3(48-71) R4(72-99) R5(100-287) R6(288-503)
    2: [   6.80,    6.80,     3.90,     3.30,     2.35,       1.40  ],
    3: [   8.80,    8.80,     5.90,     4.39,     3.30,       1.75  ],
    4: [   9.90,    9.90,     6.90,     5.25,     4.19,       2.25  ],
  },
  drinkware: {
    2: [0.80, 0.80, 0.80, 0.80, 0.80, 0.80],
    3: [1.60, 1.60, 1.60, 1.60, 1.60, 1.60],
    4: [2.40, 2.40, 2.40, 2.40, 2.40, 2.40],
  },
  tumblers: {
    2: [0.80, 0.80, 0.80, 0.80, 0.80, 0.80],
    3: [1.60, 1.60, 1.60, 1.60, 1.60, 1.60],
    4: [2.40, 2.40, 2.40, 2.40, 2.40, 2.40],
  },
  'travel-mugs': {
    2: [0.80, 0.80, 0.80, 0.80, 0.80, 0.80],
    3: [1.60, 1.60, 1.60, 1.60, 1.60, 1.60],
    4: [2.40, 2.40, 2.40, 2.40, 2.40, 2.40],
  },
  'water-bottles': {
    2: [0.80, 0.80, 0.80, 0.80, 0.80, 0.80],
    3: [1.60, 1.60, 1.60, 1.60, 1.60, 1.60],
    4: [2.40, 2.40, 2.40, 2.40, 2.40, 2.40],
  },
  'writing-instruments': { 2: [0.25, 0.25, 0.25, 0.25, 0.25, 0.25] },
  lanyards:              { 2: [0.25, 0.25, 0.25, 0.25, 0.25, 0.25] },
  'stress-relievers':    { 2: [0.25, 0.25, 0.25, 0.25, 0.25, 0.25] },
  'bottle-openers': {
    2: [0.50, 0.50, 0.50, 0.50, 0.50],
    3: [1.00, 1.00, 1.00, 1.00, 1.00],
    4: [1.50, 1.50, 1.50, 1.50, 1.50],
  },
  sunglasses: {
    2: [0.70, 0.62, 0.54, 0.46, 0.30],
    3: [1.40, 1.24, 1.08, 0.92, 0.60],
    4: [2.10, 1.86, 1.62, 1.38, 0.90],
  },
  'flying-disks': {
    2: [0.65, 0.65, 0.65, 0.65, 0.65],
    3: [1.00, 1.00, 1.00, 1.00, 1.00],
    4: [1.00, 1.00, 1.00, 1.00, 1.00],
  },
  golf: {
    2: [4.50, 4.50, 4.50, 4.50, 4.50, 4.50],
    3: [4.50, 4.50, 4.50, 4.50, 4.50, 4.50],
    4: [4.50, 4.50, 4.50, 4.50, 4.50, 4.50],
  },
  'can-coolers': {
    2: [0.30, 0.30, 0.30, 0.30, 0.30],
    3: [0.60, 0.60, 0.60, 0.60, 0.60],
    4: [0.90, 0.90, 0.90, 0.90, 0.90],
  },
  'stuffed-animals': {
    2: [0.35, 0.35, 0.35, 0.35, 0.35],
    3: [0.70, 0.70, 0.70, 0.70, 0.70],
    4: [1.05, 1.05, 1.05, 1.05, 1.05],
  },
  // No color upcharge data: candles, magnets, ice-scrapers, beach-towels, flashlights, buttons, blankets
  candles: {}, magnets: {}, 'ice-scrapers': {}, 'beach-towels': {},
  flashlights: {}, buttons: {}, blankets: {},
};

// ── ADDITIONAL LOCATION UPCHARGES — apparel only ──────────────────────────────
// Structure: LOCATION_UPCHARGES[breakIndex][extraLocations][colorCount]
// extraLocations = total selected minus 1 (so 2 locations = +1, 3 locations = +2)

const LOCATION_UPCHARGES = {
  0: { 1: { 2: 13.60, 3: 17.60, 4: 19.80 }, 2: { 2: 20.40, 3: 26.40, 4: 29.70 } }, // R1
  1: { 1: { 2: 13.60, 3: 17.60, 4: 19.80 }, 2: { 2: 20.40, 3: 26.40, 4: 29.70 } }, // R2
  2: { 1: { 2:  7.80, 3: 11.80, 4: 13.80 }, 2: { 2: 11.70, 3: 17.70, 4: 20.70 } }, // R3
  3: { 1: { 2:  6.60, 3:  8.78, 4: 10.50 }, 2: { 2:  9.90, 3: 13.17, 4: 15.75 } }, // R4
  4: { 1: { 2:  4.70, 3:  6.60, 4:  8.38 }, 2: { 2:  7.05, 3:  9.90, 4: 12.57 } }, // R5
  5: { 1: { 2:  2.80, 3:  3.50, 4:  4.50 }, 2: { 2:  4.20, 3:  5.25, 4:  6.75 } }, // R6
};

// ── STATIC ADD-ON UPCHARGES — per unit by break index ─────────────────────────
const ADDON_UPCHARGES = {
  //              R1      R2      R3     R4     R5     R6
  hatPatch:     [11.30,  11.30,  10.00,  8.04,  7.34,  6.14],
  goldRim:      [ 2.32,   2.32,   2.32,  2.32,  2.32,  2.32],
  laserEtchSide:[ 3.00,   3.00,   3.00,  2.50,  2.50,  2.50],
  laserEtchInside:[1.25,  1.25,   1.00,  1.00,  0.75,  0.75],
  preciousMetal:[ 2.32,   2.32,   2.32,  2.32,  2.32,  2.32],
};

// Per-order add-on (flat, not per unit)
const COLOR_MATCH_PER_COLOR = 22.00;

// Max colors before quote trigger
const MAX_COLORS = {
  apparel: 4, drinkware: 4, tumblers: 4, 'travel-mugs': 4, 'water-bottles': 4,
  candles: 1, 'writing-instruments': 2, lanyards: 2, 'stress-relievers': 2,
  magnets: 1, 'bottle-openers': 4, 'ice-scrapers': 1, sunglasses: 4,
  'flying-disks': 4, 'beach-towels': 1, blankets: 0, golf: 4,
  'can-coolers': 4, flashlights: 1, 'stuffed-animals': 4, buttons: 1,
};

const ALWAYS_QUOTE_TYPES = ['blankets'];

/* ============================================================
   ENGINE — no edits needed below this line
   ============================================================ */

(function () {
  'use strict';

  // Current quantity entered by the customer
  let currentQty = 1;

  // ── Detect product type ───────────────────────────────────────────────────────
  function detectProductType() {
    if (window.mainstProductType) return window.mainstProductType;
    const meta = document.querySelector('[data-mainst-product-type]');
    if (meta) return meta.dataset.mainstProductType;

    // Pillar theme: check ShopifyAnalytics
    try {
      const tags = window.ShopifyAnalytics?.meta?.product?.tags || [];
      for (const tag of tags) {
        if (tag.startsWith('pricing:')) return tag.replace('pricing:', '');
      }
    } catch (e) {}

    // Fallback: check all script[type="application/json"] tags for product data
    try {
      const scripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        const text = script.textContent.trim();
        if (text.includes('"price"') && text.includes('"title"') && text.includes('QTY')) {
          const data = JSON.parse(text);
          const tags = Array.isArray(data) ? [] : (data.tags || []);
          for (const tag of tags) {
            if (tag.startsWith('pricing:')) return tag.replace('pricing:', '');
          }
        }
      }
    } catch (e) {}

    // Fallback: check pageSpecificData
    try {
      const scripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        const text = script.textContent.trim();
        if (text.includes('pageSpecificData')) {
          const data = JSON.parse(text);
          const tags = data?.pageSpecificData?.resource?.tags || [];
          for (const tag of tags) {
            if (tag.startsWith('pricing:')) return tag.replace('pricing:', '');
          }
        }
      }
    } catch (e) {}

    return 'apparel'; // safe default
  }

  // ── Resolve qty range from a number ──────────────────────────────────────────
  // Returns { variantLabel, breakIndex } or null
  function resolveQtyRange(qty, productType) {
    const ranges = QTY_RANGES[productType] || QTY_RANGES['apparel'];
    for (const [min, max, label, breakIndex] of ranges) {
      if (qty >= min && qty <= max) {
        return { variantLabel: label, breakIndex };
      }
    }
    return null;
  }

  // ── Auto-select Shopify variant based on qty + current garment color ──────────
  function autoSelectVariant(variantLabel) {
    // Find the Quantity Range variant buttons/inputs and click the right one
    const inputs = document.querySelectorAll(
      '.product-form__input input[type="radio"], .variant-input input[type="radio"]'
    );
    for (const input of inputs) {
      const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
      const text  = label?.textContent?.trim() || input.value?.trim();
      if (text === variantLabel) {
        if (!input.checked) {
          input.click();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
    }
    // Fallback: select element
    const selects = document.querySelectorAll('select.product-form__variants, select[name="id"]');
    for (const sel of selects) {
      for (const opt of sel.options) {
        if (opt.text.includes(variantLabel)) {
          sel.value = opt.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }

  // ── Count selected imprint locations ─────────────────────────────────────────
  function countImprintLocations() {
    const allLabels = document.querySelectorAll('label, .globo-label, .option-name, [class*="label"]');
    for (const label of allLabels) {
      if (label.textContent.trim().toLowerCase() === GLOBO_FIELD_LABELS.imprintLocation.toLowerCase()) {
        const container = label.closest(
          '.globo-option-item, .option-item, .option-group, .form-group, [class*="option"]'
        ) || label.parentElement;
        if (!container) continue;
        // Multi-select dropdown — count selected options
        const select = container.querySelector('select[multiple], select');
        if (select) {
          return Array.from(select.selectedOptions).length;
        }
        // Checkboxes
        const checked = container.querySelectorAll('input[type="checkbox"]:checked');
        if (checked.length) return checked.length;
        // Globo multi-select pills/buttons
        const selected = container.querySelectorAll('.selected, .active, [aria-selected="true"], .globo-selected');
        if (selected.length) return selected.length;
      }
    }
    return 1; // default to 1 if can't detect
  }

  // ── Read a Globo option value by label ────────────────────────────────────────
  function getGloboValue(labelText) {
    const allLabels = document.querySelectorAll('label, .globo-label, .option-name, [class*="label"]');
    for (const label of allLabels) {
      if (label.textContent.trim().toLowerCase() === labelText.toLowerCase()) {
        const container = label.closest(
          '.globo-option-item, .option-item, .option-group, .form-group, [class*="option"]'
        ) || label.parentElement;
        if (!container) continue;
        const select = container.querySelector('select');
        if (select) return select.value;
        const checkedRadio = container.querySelector('input[type="radio"]:checked');
        if (checkedRadio) return checkedRadio.value || checkedRadio.nextElementSibling?.textContent?.trim();
        const checkbox = container.querySelector('input[type="checkbox"]');
        if (checkbox) return checkbox.checked ? 'yes' : 'no';
      }
    }
    return null;
  }

  function isAddonChecked(labelText) {
    return getGloboValue(labelText) === 'yes';
  }

  // ── Get all variants from Pillar theme data ──────────────────────────────────
  function getAllVariants() {
    // Pillar stores variants in ShopifyAnalytics
    try {
      const variants = window.ShopifyAnalytics?.meta?.product?.variants;
      if (variants?.length) return variants;
    } catch (e) {}

    // Fallback: parse from script tags — find the one with full variant array
    try {
      const scripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        const text = script.textContent.trim();
        if (text.startsWith('[') && text.includes('"price"') && text.includes('QTY')) {
          const data = JSON.parse(text);
          if (Array.isArray(data) && data[0]?.price !== undefined) return data;
        }
      }
    } catch (e) {}
    return [];
  }

  // ── Get base price for a given qty range label and current garment color ──────
  function getBasePriceForVariant(variantLabel) {
    const variants = getAllVariants();
    if (!variants.length) return 0;

    // Get currently selected garment color from Pillar's variant selector
    const colorInput = document.querySelector(
      '.product__block--variant_picker input[type="radio"]:checked, [data-option="Garment Color"] input:checked'
    );
    const selectedColor = colorInput?.value?.trim() ||
      document.querySelector('.product__block--variant_picker .selected, .product__block--variant_picker [aria-pressed="true"]')
        ?.textContent?.trim();

    // Find variant matching qty label + color
    for (const v of variants) {
      const title = v.public_title || v.title || '';
      if (title.includes(variantLabel)) {
        if (!selectedColor || title.includes(selectedColor)) {
          return v.price / 100;
        }
      }
    }

    // Fallback: just match qty label
    for (const v of variants) {
      const title = v.public_title || v.title || '';
      if (title.includes(variantLabel)) {
        return v.price / 100;
      }
    }

    return 0;
  }

  // ── Get Shopify base price from selected variant ──────────────────────────────
  function getBasePrice() {
    // Try Pillar's price display element first
    const priceEl = document.querySelector(
      '.price__current, .product__price .price, [data-product-price], .price-item--regular'
    );
    if (priceEl) {
      const parsed = parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }

    // Fallback: read from currently selected variant in ShopifyAnalytics
    try {
      const variants = getAllVariants();
      const currentVariantId = document.querySelector('input.product-variant-id, [name="id"]')?.value;
      if (currentVariantId) {
        const variant = variants.find(v => v.id == currentVariantId);
        if (variant) return variant.price / 100;
      }
    } catch (e) {}

    return 0;
  }

  function formatPrice(n) { return '$' + n.toFixed(2); }

  // ── Size breakdown widget ────────────────────────────────────────────────────
  const SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];

  function injectSizeBreakdown() {
    if (document.getElementById('mainst-size-breakdown')) return;

    const wrapper = document.createElement('div');
    wrapper.id = 'mainst-size-breakdown';
    wrapper.style.cssText = 'margin:12px 0;clear:both;width:100%;display:block;';
    wrapper.innerHTML = `
      <label style="display:block;font-size:14px;font-weight:500;margin-bottom:8px;">
        Size Breakdown
      </label>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
        ${SIZES.map(size => `
          <div style="text-align:center;">
            <div style="font-size:11px;font-weight:600;color:#555;margin-bottom:3px;">${size}</div>
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              id="mainst-size-${size.replace('XL','xl').replace('X','x')}"
              data-size="${size}"
              data-mainst-size="true"
              value="0"
              style="width:44px;padding:6px 4px;border:1px solid #ccc;border-radius:6px;font-size:14px;text-align:center;"
            />
          </div>
        `).join('')}
      </div>
      <div id="mainst-size-total" style="margin-top:8px;font-size:13px;color:#666;"></div>
    `;

    // Insert after qty wrapper
    const qtyWrapper = document.getElementById('mainst-qty-wrapper');
    if (qtyWrapper) {
      qtyWrapper.insertAdjacentElement('afterend', wrapper);
    } else {
      const gpoContainer = document.querySelector('.gpo-container');
      if (gpoContainer) gpoContainer.insertAdjacentElement('beforebegin', wrapper);
    }

    // Listen for size input changes
    wrapper.querySelectorAll('input[data-mainst-size]').forEach(input => {
      input.addEventListener('input', function() {
        const val = parseInt(this.value.replace(/[^0-9]/g, '')) || 0;
        this.value = val === 0 ? '0' : val;
        updateSizeTotal();
      });
      input.addEventListener('focus', function() {
        if (this.value === '0') this.value = '';
      });
      input.addEventListener('blur', function() {
        if (!this.value) this.value = '0';
        updateSizeTotal();
      });
    });
  }

  function updateSizeTotal() {
    const total = getSizeTotal();
    const qty   = currentQty || 0;
    const el    = document.getElementById('mainst-size-total');
    if (!el) return;

    if (qty === 0) {
      el.textContent = '';
      return;
    }

    if (total === 0) {
      el.style.color = '#666';
      el.textContent = 'Enter quantities per size (must total ' + qty + ')';
    } else if (total === qty) {
      el.style.color = '#16a34a';
      el.textContent = '✓ Total: ' + total + ' of ' + qty;
    } else if (total < qty) {
      el.style.color = '#f59e0b';
      el.textContent = 'Total: ' + total + ' of ' + qty + ' — need ' + (qty - total) + ' more';
    } else {
      el.style.color = '#e53e3e';
      el.textContent = 'Total: ' + total + ' of ' + qty + ' — ' + (total - qty) + ' too many';
    }
  }

  function getSizeTotal() {
    let total = 0;
    document.querySelectorAll('input[data-mainst-size]').forEach(input => {
      total += parseInt(input.value) || 0;
    });
    return total;
  }

  function getSizeBreakdown() {
    const parts = [];
    document.querySelectorAll('input[data-mainst-size]').forEach(input => {
      const val = parseInt(input.value) || 0;
      if (val > 0) parts.push(val + input.dataset.size);
    });
    return parts.join(', ');
  }

  // ── Smart quantity input ──────────────────────────────────────────────────────
  function injectQtyInput() {
    if (document.getElementById('mainst-qty-input')) return;

    // Hide the Quantity Range variant group
    // Pillar wraps each option group in .product-form__input--button
    document.querySelectorAll('.product-form__input--button').forEach(el => {
      const hasQty = el.querySelector('input[value^="QTY"]');
      if (hasQty) el.style.display = 'none';
    });

    // Also hide the Quantity Range variant pills — customer doesn't need to see them
    const variantInputs = document.querySelectorAll('.product-form__input');
    for (const input of variantInputs) {
      const legend = input.querySelector('legend, .form__label');
      if (legend?.textContent?.trim().toLowerCase() === 'quantity range') {
        input.style.display = 'none';
      }
    }

    const wrapper = document.createElement('div');
    wrapper.id = 'mainst-qty-wrapper';
    wrapper.style.cssText = 'margin:12px 0;';
    wrapper.innerHTML = `
      <label for="mainst-qty-input" style="display:block;font-size:14px;font-weight:500;margin-bottom:6px;">
        How many items?
      </label>
      <div style="display:flex;align-items:center;gap:8px;">
        <input
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          id="mainst-qty-input"
          data-mainst-qty="true"
          value="1"
          style="width:90px;padding:8px 12px;border:1px solid #ccc;border-radius:6px;font-size:16px;text-align:center;"
        />
        <span id="mainst-qty-range-hint" style="font-size:13px;color:#666;"></span>
      </div>
    `;

    // Insert before Globo container inside product-form__buttons
    const globoContainer = document.querySelector('.gpo-container, .gpo-app, .globo-product-options');
    const productFormButtons = document.querySelector('#QuantityForm-template--20983372873961__main, .product-form__buttons');
    const addToCart = document.querySelector('[name="add"], .product-form__submit, button[type="submit"]');

    if (globoContainer) {
      // Insert directly before the Globo container
      globoContainer.parentElement.insertBefore(wrapper, globoContainer);
    } else if (productFormButtons) {
      productFormButtons.insertBefore(wrapper, productFormButtons.firstChild);
    } else if (addToCart?.parentElement) {
      addToCart.parentElement.insertBefore(wrapper, addToCart);
    }

    // Listen for qty changes — accepts text input to avoid Dawn's quantity-input wrapper
    document.getElementById('mainst-qty-input').addEventListener('input', function () {
      const val = parseInt(this.value.replace(/[^0-9]/g, ''));
      if (!isNaN(val) && val > 0) {
        currentQty = val;
        recalculate();
        updateSizeTotal();
      }
    });
    // Prevent non-numeric characters
    document.getElementById('mainst-qty-input').addEventListener('keypress', function (e) {
      if (!/[0-9]/.test(e.key)) e.preventDefault();
    });
  }

  // ── Get all Add to Cart buttons in the product form ─────────────────────────
  function getCartButtons() {
    return document.querySelectorAll(
      '.main-product-form button[type="submit"], .main-product-form [name="add"], .product-form__buttons button[type="submit"]'
    );
  }

  // ── Build quote URL with pre-filled parameters ────────────────────────────────
  function buildQuoteUrl(reason) {
    const productName  = document.querySelector('h1, .product__title')?.textContent?.trim() || '';
    const qty          = document.getElementById('mainst-qty-input')?.value || '1';
    const qtyRange     = document.getElementById('mainst-qty-range-hint')?.textContent?.replace(/[()]/g, '').trim() || '';
    const numColors    = getGloboValue(GLOBO_FIELD_LABELS.numColors) || '1 color';
    const decoration   = getGloboValue(GLOBO_FIELD_LABELS.decorationType) || '';
    // Get garment color from selected variant ID — more reliable than reading checked inputs
    let garmentColor = '';
    try {
      const variantId = document.querySelector('.product-variant-id')?.value
        || new URLSearchParams(window.location.search).get('variant');
      if (variantId) {
        const variants = getAllVariants();
        const variant = variants.find(v => String(v.id) === String(variantId));
        if (variant) {
          // Variant title is "QTY 24-47 / Red" — extract color after the /
          const parts = (variant.public_title || variant.title || '').split('/');
          if (parts.length > 1) garmentColor = parts[parts.length - 1].trim();
        }
      }
    } catch(e) {}
    const locationInputs = document.querySelectorAll('input[data-field-name="dropdown-1"]:checked');
    const locations    = Array.from(locationInputs).map(i => i.value).join(', ');
    const colorMatch   = isAddonChecked(GLOBO_FIELD_LABELS.colorMatch) ? 'Yes' : 'No';
    const priceBox     = document.getElementById('mainst-price-display');
    const priceText    = priceBox ? priceBox.innerText.replace(/\n+/g, ' | ').trim() : '';

    const params = new URLSearchParams({
      product:       productName,
      quantity:      qty,
      qty_range:     qtyRange,
      colors:        numColors,
      decoration:    decoration,
      garment_color: garmentColor,
      locations:     locations,
      color_match:   colorMatch,
      pricing:       priceText,
      reason:        reason,
    });
    return `/pages/auto-quote?${params.toString()}`;
  }

  // ── Quote prompt UI ───────────────────────────────────────────────────────────
  function showQuotePrompt(reason) {
    // Hide our checkout button (native buttons already hidden)
    const checkoutBtn = document.getElementById('mainst-checkout-btn');
    if (checkoutBtn) checkoutBtn.style.display = 'none';

    // Hide the price display, show quote in the sticky panel instead
    const priceDisplay = document.getElementById('mainst-price-display');
    if (priceDisplay) priceDisplay.style.display = 'none';

    // Update mobile bar to show quote CTA
    const mobileBar = document.getElementById('mainst-mobile-bar');
    if (mobileBar) {
      mobileBar.style.background = '#1B3A6B';
      mobileBar.innerHTML = `
        <span>Custom quote required</span>
        <a id="mainst-mobile-quote-btn" href="#" style="color:white;font-weight:700;text-decoration:none;font-size:14px;">
          Request a Quote →
        </a>`;
      // Build URL at click time
      const mobileQuoteBtn = document.getElementById('mainst-mobile-quote-btn');
      if (mobileQuoteBtn) {
        mobileQuoteBtn.addEventListener('click', function(e) {
          e.preventDefault();
          window.location.href = buildQuoteUrl(reason);
        });
      }
    }

    // Show quote prompt inside the sticky wrapper
    const sticky = document.getElementById('mainst-sticky-wrapper');
    if (sticky) {
      sticky.style.display = '';
      let el = document.getElementById('mainst-quote-prompt');
      if (!el) {
        el = document.createElement('div');
        el.id = 'mainst-quote-prompt';
        sticky.appendChild(el);
      }
      el.style.cssText = 'background:#fff8e1;border:1px solid #f59e0b;border-radius:10px;padding:16px 18px;font-size:14px;color:#92400e;box-shadow:0 4px 20px rgba(0,0,0,0.12);';
      el.innerHTML = `
        <div style="font-size:11px;font-weight:600;letter-spacing:0.05em;color:#92400e;text-transform:uppercase;margin-bottom:8px;">Custom Quote Required</div>
        <div style="font-size:13px;margin-bottom:12px;">${reason}</div>
        <a id="mainst-quote-btn" href="#"
          style="display:block;text-align:center;background:#1B3A6B;color:white;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600;font-size:14px;">
          Request a Quote →
        </a>`;
      el.style.display = 'block';
      // Build URL at click time so it always reads the current variant state
      const quoteBtn = document.getElementById('mainst-quote-btn');
      if (quoteBtn) {
        quoteBtn.addEventListener('click', function(e) {
          e.preventDefault();
          window.location.href = buildQuoteUrl(reason);
        });
      }
    }
  }

  function hideQuotePrompt() {
    const el = document.getElementById('mainst-quote-prompt');
    if (el) el.style.display = 'none';
    // Restore our checkout button (not native buttons — those stay hidden)
    const checkoutBtn = document.getElementById('mainst-checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.style.display = 'block';
      checkoutBtn.disabled = false;
      checkoutBtn.textContent = 'Add to Cart';
      checkoutBtn.style.background = '#1B3A6B';
    }
    // Restore sticky panel
    const sticky = document.getElementById('mainst-sticky-wrapper');
    if (sticky) sticky.style.display = '';
    // Restore mobile bar
    const mobileBar = document.getElementById('mainst-mobile-bar');
    if (mobileBar) {
      mobileBar.style.background = '#1B3A6B';
      mobileBar.innerHTML = `
        <span>View pricing</span>
        <span id="mainst-mobile-total" style="font-size:16px;">—</span>
        <span id="mainst-mobile-chevron">▲</span>`;
    }
  }

  // ── Price display UI ──────────────────────────────────────────────────────────
  function ensurePriceDisplay() {
    if (document.getElementById('mainst-price-display')) return;

    // Inject responsive CSS
    const style = document.createElement('style');
    style.textContent = `
      #mainst-sticky-wrapper {
        position: fixed;
        top: 80px;
        right: 24px;
        width: 280px;
        z-index: 998;
      }
      #mainst-price-display {
        background: #f0f4ff;
        border: 1px solid #c7d3f0;
        border-radius: 10px;
        padding: 16px 18px;
        font-size: 14px;
        display: none;
        box-shadow: 0 4px 20px rgba(0,0,0,0.12);
      }
      /* Mobile — slide up from bottom */
      @media (max-width: 768px) {
        #mainst-sticky-wrapper {
          position: fixed;
          top: auto !important;
          bottom: 0;
          right: 0;
          left: 0;
          width: 100%;
          padding: 0;
          z-index: 9999;
        }
        #mainst-price-display {
          border-radius: 12px 12px 0 0;
          border-bottom: none;
          padding: 14px 16px;
          font-size: 13px;
          display: block !important;
        }
        #mainst-mobile-bar {
          display: flex !important;
          justify-content: space-between;
          align-items: center;
          background: #1B3A6B;
          color: white;
          padding: 12px 16px;
          cursor: pointer;
          border-radius: 12px 12px 0 0;
          font-size: 14px;
          font-weight: 600;
        }
        #mainst-price-display.collapsed {
          display: none !important;
        }
      }
      @media (min-width: 769px) {
        #mainst-mobile-bar { display: none !important; }
      }
    `;
    document.head.appendChild(style);

    const sticky = document.createElement('div');
    sticky.id = 'mainst-sticky-wrapper';

    // Mobile collapsed bar — shows order total, tap to expand
    const mobileBar = document.createElement('div');
    mobileBar.id = 'mainst-mobile-bar';
    mobileBar.innerHTML = `
      <span>View pricing</span>
      <span id="mainst-mobile-total" style="font-size:16px;">—</span>
      <span id="mainst-mobile-chevron">▲</span>
    `;
    let mobileExpanded = false;
    mobileBar.addEventListener('click', () => {
      mobileExpanded = !mobileExpanded;
      const display = document.getElementById('mainst-price-display');
      display.classList.toggle('collapsed', !mobileExpanded);
      document.getElementById('mainst-mobile-chevron').textContent = mobileExpanded ? '▼' : '▲';
    });

    const el = document.createElement('div');
    el.id = 'mainst-price-display';
    el.classList.add('collapsed'); // start collapsed on mobile

    sticky.appendChild(mobileBar);
    sticky.appendChild(el);
    document.body.appendChild(sticky);

    // Dynamically position below actual header height on desktop
    function positionStickyPanel() {
      if (window.innerWidth > 768) {
        const header = document.querySelector('header, .header, #header, [class*="site-header"], [class*="header--main"]');
        const headerBottom = header ? header.getBoundingClientRect().bottom + window.scrollY + 16 : 80;
        sticky.style.top = Math.max(headerBottom, 80) + 'px';
      }
    }
    positionStickyPanel();
    window.addEventListener('resize', positionStickyPanel);

    // On mobile, show the bar immediately with placeholder text
    if (window.innerWidth <= 768) {
      mobileBar.style.display = 'flex';
      const mobileTotalEl = document.getElementById('mainst-mobile-total');
      if (mobileTotalEl) mobileTotalEl.textContent = 'Select options';
    }
  }

  function renderPriceDisplay({ basePrice, colorUpcharge, locationUpcharge, addonUpcharge, qty, colorMatchCount, qtyLabel }) {
    const el = document.getElementById('mainst-price-display');
    if (!el) return;
    const perUnit         = basePrice + colorUpcharge + locationUpcharge + addonUpcharge;
    const itemTotal       = perUnit * qty;
    const colorMatchTotal = colorMatchCount * COLOR_MATCH_PER_COLOR;
    const orderTotal      = itemTotal + colorMatchTotal;

    const row = (label, val) =>
      `<div style="display:flex;justify-content:space-between;margin-bottom:6px;">
         <span style="color:#666;">${label}</span><span>${val}</span>
       </div>`;

    // Update qty range hint next to input
    const hint = document.getElementById('mainst-qty-range-hint');
    if (hint) hint.textContent = qtyLabel ? `(${qtyLabel})` : '';

    // Update mobile bar total
    const mobileTotal = document.getElementById('mainst-mobile-total');
    if (mobileTotal) mobileTotal.textContent = formatPrice(orderTotal);
    const mobileBar = document.getElementById('mainst-mobile-bar');
    if (mobileBar) mobileBar.style.display = 'flex';

    // Desktop: always visible. Mobile: controlled by tap on bar.
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      el.style.display = 'block';
      el.classList.remove('collapsed');
    } else {
      // Keep collapsed state as-is — bar tap controls it
      el.style.display = 'block';
    }

    el.innerHTML = `
      <div style="font-size:11px;font-weight:600;letter-spacing:0.05em;color:#1B3A6B;text-transform:uppercase;margin-bottom:10px;">Live Pricing — ${qtyLabel || ''}</div>
      ${row('Base price', formatPrice(basePrice) + '/unit')}
      ${colorUpcharge    > 0 ? row('Color upcharge',      '+' + formatPrice(colorUpcharge)    + '/unit') : ''}
      ${locationUpcharge > 0 ? row('Additional location', '+' + formatPrice(locationUpcharge) + '/unit') : ''}
      ${addonUpcharge    > 0 ? row('Add-ons',             '+' + formatPrice(addonUpcharge)    + '/unit') : ''}
      <div style="display:flex;justify-content:space-between;border-top:1px solid #c7d3f0;padding-top:8px;margin-top:4px;font-weight:600;font-size:15px;">
        <span>Price per unit</span><span style="color:#1B3A6B;">${formatPrice(perUnit)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:4px;font-size:12px;color:#666;">
        <span>${formatPrice(perUnit)} × ${qty} items</span><span>${formatPrice(itemTotal)}</span>
      </div>
      ${colorMatchTotal > 0 ? row(`Color match (${colorMatchCount} color${colorMatchCount > 1 ? 's' : ''} × $22/order)`, '+' + formatPrice(colorMatchTotal)) : ''}
      <div style="display:flex;justify-content:space-between;border-top:1px solid #c7d3f0;padding-top:8px;margin-top:6px;font-weight:700;font-size:17px;">
        <span>Order total</span><span style="color:#1B3A6B;">${formatPrice(orderTotal)}</span>
      </div>
      ${colorMatchTotal > 0 ? '<div style="font-size:11px;color:#999;margin-top:4px;">* Color match fee is per order, not per unit.</div>' : ''}
    `;
  }

  // ── Main calculation ──────────────────────────────────────────────────────────
  function recalculate() {
    const productType = detectProductType();

    // Always-quote types
    if (ALWAYS_QUOTE_TYPES.includes(productType)) {
      showQuotePrompt('Blankets use embroidery which requires a stitch count quote.');
      return;
    }

    // Embroidery quote trigger
    const decorationType = getGloboValue(GLOBO_FIELD_LABELS.decorationType);
    if (decorationType === 'Embroidery') {
      showQuotePrompt('Embroidery pricing is based on stitch count and requires a custom quote.');
      return;
    }

    // Location count trigger — more than 3 locations requires a quote
    if (productType === 'apparel') {
      const locationCount = countImprintLocations();
      if (locationCount > 3) {
        showQuotePrompt('Orders with more than 3 imprint locations require a custom quote.');
        return;
      }
    }

    // Color count
    const numColorsRaw = getGloboValue(GLOBO_FIELD_LABELS.numColors) || '1';
    const colorCount   = parseInt(numColorsRaw) || 1;

    if (numColorsRaw.toLowerCase().includes('5+') || colorCount > 4) {
      showQuotePrompt('Orders with 5 or more imprint colors require a custom quote.');
      return;
    }

    const maxColors = MAX_COLORS[productType] ?? 4;
    if (colorCount > maxColors) {
      showQuotePrompt(`${productType.replace(/-/g,' ')} orders with more than ${maxColors} color${maxColors === 1 ? '' : 's'} require a custom quote.`);
      return;
    }

    // Resolve qty range from typed number
    const resolved = resolveQtyRange(currentQty, productType);
    if (!resolved) {
      hideQuotePrompt();
      const el = document.getElementById('mainst-price-display');
      if (el) el.style.display = 'none';
      return;
    }

    const { variantLabel, breakIndex } = resolved;

    // Quote trigger
    if (breakIndex === 'quote') {
      showQuotePrompt('This quantity requires a custom quote. Please contact us for pricing.');
      return;
    }

    // Auto-select the correct Shopify variant silently
    autoSelectVariant(variantLabel);

    hideQuotePrompt();

    // Get base price directly from variant data — no need to wait for DOM update
    const basePrice = getBasePriceForVariant(variantLabel);
    if (!basePrice) {
      // Fallback to DOM price after short delay
      setTimeout(() => {
        const fallbackPrice = getBasePrice();
        if (!fallbackPrice) return;
        finishCalculation(fallbackPrice, breakIndex, productType, colorCount, currentQty, variantLabel);
      }, 150);
      return;
    }

    // Run calculation with variant data price
    setTimeout(() => {
      finishCalculation(basePrice, breakIndex, productType, colorCount, currentQty, variantLabel);
    }, 150);
  }

  // ── Finish calculation with known base price ──────────────────────────────────
  function finishCalculation(basePrice, breakIndex, productType, colorCount, qty, variantLabel) {
    // Color upcharge
    const typeUpcharges = COLOR_UPCHARGES[productType] || {};
    const colorUpcharge = (colorCount >= 2 && typeUpcharges[colorCount])
      ? (typeUpcharges[colorCount][breakIndex] ?? 0)
      : 0;

    // Location upcharge — count selected imprint locations
    let locationUpcharge = 0;
    if (productType === 'apparel' && colorCount >= 2) {
      const locationCount = countImprintLocations();
      const extraLocations = locationCount - 1;
      if (extraLocations > 0 && LOCATION_UPCHARGES[breakIndex]?.[extraLocations]?.[colorCount]) {
        locationUpcharge = LOCATION_UPCHARGES[breakIndex][extraLocations][colorCount];
      }
    }

    // Add-on upcharges
    let addonUpcharge = 0;
    if (isAddonChecked(GLOBO_FIELD_LABELS.hatPatch))
      addonUpcharge += ADDON_UPCHARGES.hatPatch[breakIndex]        ?? 0;
    if (isAddonChecked(GLOBO_FIELD_LABELS.goldRim))
      addonUpcharge += ADDON_UPCHARGES.goldRim[breakIndex]         ?? 0;
    if (isAddonChecked(GLOBO_FIELD_LABELS.laserEtchSide))
      addonUpcharge += ADDON_UPCHARGES.laserEtchSide[breakIndex]   ?? 0;
    if (isAddonChecked(GLOBO_FIELD_LABELS.laserEtchInside))
      addonUpcharge += ADDON_UPCHARGES.laserEtchInside[breakIndex] ?? 0;
    if (isAddonChecked(GLOBO_FIELD_LABELS.preciousMetal))
      addonUpcharge += ADDON_UPCHARGES.preciousMetal[breakIndex]   ?? 0;

    // Color match per-order add-on
    const colorMatchCount = isAddonChecked(GLOBO_FIELD_LABELS.colorMatch) ? colorCount : 0;

    renderPriceDisplay({
      basePrice,
      colorUpcharge,
      locationUpcharge,
      addonUpcharge,
      qty,
      colorMatchCount,
      qtyLabel: variantLabel,
    });
  }

  // ── Event listeners ───────────────────────────────────────────────────────────
  // Re-hide QTY variant picker after Pillar re-renders on color change
  function hideQtyVariants() {
    document.querySelectorAll('.product-form__input--button').forEach(function(el) {
      if (el.querySelector('input[value^="QTY"]')) el.style.display = 'none';
    });
  }

  function attachListeners() {
    document.addEventListener('change', function(e) {
      if (['SELECT', 'INPUT'].includes(e.target.tagName) && e.target.id !== 'mainst-qty-input') {
        recalculate();
        setTimeout(hideQtyVariants, 50);
        setTimeout(hideQtyVariants, 300);
      }
    });
    document.addEventListener('variant:changed', function() {
      recalculate();
      setTimeout(hideQtyVariants, 50);
      setTimeout(hideQtyVariants, 300);
    });
  }

  // ── Worker URL ────────────────────────────────────────────────────────────────
  const WORKER_URL = 'https://mainst-price-worker.accountmanagement-d7b.workers.dev';

  // ── Block Globo's direct /cart/add.js calls ──────────────────────────────────
  function blockGloboCartAdd() {
    // Override fetch to intercept /cart/add.js
    const originalFetch = window.fetch;
    window.fetch = function(url, opts) {
      if (typeof url === 'string' && url.includes('/cart/add')) {
        const priceDisplay = document.getElementById('mainst-price-display');
        if (priceDisplay && priceDisplay.style.display !== 'none') {
          console.log('mainst: blocked /cart/add.js call from Globo');
          // Trigger our checkout button instead
          setTimeout(() => document.getElementById('mainst-checkout-btn')?.click(), 0);
          // Return a fake successful response to keep Globo happy
          return Promise.resolve(new Response(JSON.stringify({ items: [] }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }));
        }
      }
      return originalFetch.apply(this, arguments);
    };

    // Also override XMLHttpRequest for older code paths
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      if (typeof url === 'string' && url.includes('/cart/add')) {
        const priceDisplay = document.getElementById('mainst-price-display');
        if (priceDisplay && priceDisplay.style.display !== 'none') {
          console.log('mainst: blocked XHR /cart/add call');
          this._mainstBlocked = true;
        }
      }
      return originalOpen.apply(this, arguments);
    };

    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function() {
      if (this._mainstBlocked) {
        setTimeout(() => document.getElementById('mainst-checkout-btn')?.click(), 0);
        return;
      }
      return originalSend.apply(this, arguments);
    };
  }

  // ── Replace Add to Cart with our own Checkout button ────────────────────────
  function injectCheckoutButton() {
    if (document.getElementById('mainst-checkout-btn')) return;

    // Hide all native Add to Cart buttons
    document.querySelectorAll('button[name="add"]').forEach(btn => {
      btn.style.display = 'none';
    });

    // Also hide Buy Now / dynamic checkout buttons
    const paymentBtn = document.querySelector('[data-shopify="payment-button"], .shopify-payment-button');
    if (paymentBtn) paymentBtn.style.display = 'none';

    // Make sure sticky price panel stays visible
    const sticky = document.getElementById('mainst-sticky-wrapper');
    if (sticky) sticky.style.display = '';

    // Create our replacement button
    const btn = document.createElement('button');
    btn.id = 'mainst-checkout-btn';
    btn.type = 'button';
    btn.textContent = 'Proceed to Checkout';
    btn.style.cssText = `
      display: block;
      width: 100%;
      padding: 14px;
      background: #1B3A6B;
      color: white;
      border: none;
      border-radius: 6px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      margin-top: 8px;
      transition: background 0.15s;
    `;
    btn.onmouseover = () => btn.style.background = '#152d54';
    btn.onmouseout  = () => btn.style.background = '#1B3A6B';

    btn.addEventListener('click', async function() {
      // If no calculated price showing — fall back to native form submit
      const priceDisplay = document.getElementById('mainst-price-display');
      if (!priceDisplay || priceDisplay.style.display === 'none') {
        // Show native buttons and submit
        document.querySelectorAll('button[name="add"]').forEach(b => b.style.display = '');
        document.querySelector('button[name="add"].gpo-original-atc-button')?.click();
        return;
      }

      const variantId   = parseInt(document.querySelector('.product-variant-id')?.value);
      const qty         = currentQty || 1;
      const productType = detectProductType();
      const resolved    = resolveQtyRange(qty, productType);

      if (!resolved || resolved.breakIndex === 'quote') {
        document.querySelectorAll('button[name="add"]').forEach(b => b.style.display = '');
        document.querySelector('button[name="add"].gpo-original-atc-button')?.click();
        return;
      }

      const { variantLabel, breakIndex } = resolved;
      const numColorsRaw  = getGloboValue(GLOBO_FIELD_LABELS.numColors) || '1';
      const colorCount    = parseInt(numColorsRaw) || 1;
      const colorMatchChecked = isAddonChecked(GLOBO_FIELD_LABELS.colorMatch);
      const colorMatchTotal   = colorMatchChecked ? colorCount * 22.00 : 0;

      // Read per-unit price directly from the sticky price display
      // This is the most reliable source — it's exactly what the customer sees
      let calculatedPrice = 0;
      document.querySelectorAll('#mainst-price-display > div').forEach(row => {
        const spans = row.querySelectorAll('span');
        if (spans.length >= 2 && spans[0].textContent.trim() === 'Price per unit') {
          calculatedPrice = parseFloat(spans[1].textContent.replace(/[^0-9.]/g, ''));
        }
      });

      // Fallback if display reading failed
      if (!calculatedPrice) {
        const basePrice     = getBasePriceForVariant(variantLabel);
        const typeUpcharges = COLOR_UPCHARGES[productType] || {};
        const colorUpcharge = (colorCount >= 2 && typeUpcharges[colorCount])
          ? (typeUpcharges[colorCount][breakIndex] ?? 0) : 0;
        let locationUpcharge = 0;
        if (productType === 'apparel' && colorCount >= 2) {
          const locationCount  = countImprintLocations();
          const extraLocations = locationCount - 1;
          if (extraLocations > 0 && LOCATION_UPCHARGES[breakIndex]?.[extraLocations]?.[colorCount]) {
            locationUpcharge = LOCATION_UPCHARGES[breakIndex][extraLocations][colorCount];
          }
        }
        let addonUpcharge = 0;
        if (isAddonChecked(GLOBO_FIELD_LABELS.hatPatch))        addonUpcharge += ADDON_UPCHARGES.hatPatch[breakIndex]        ?? 0;
        if (isAddonChecked(GLOBO_FIELD_LABELS.goldRim))         addonUpcharge += ADDON_UPCHARGES.goldRim[breakIndex]         ?? 0;
        if (isAddonChecked(GLOBO_FIELD_LABELS.laserEtchSide))   addonUpcharge += ADDON_UPCHARGES.laserEtchSide[breakIndex]   ?? 0;
        if (isAddonChecked(GLOBO_FIELD_LABELS.laserEtchInside)) addonUpcharge += ADDON_UPCHARGES.laserEtchInside[breakIndex] ?? 0;
        if (isAddonChecked(GLOBO_FIELD_LABELS.preciousMetal))   addonUpcharge += ADDON_UPCHARGES.preciousMetal[breakIndex]   ?? 0;
        calculatedPrice = basePrice + colorUpcharge + locationUpcharge + addonUpcharge;
      }


      let garmentColor = '';
      try {
        const variants = getAllVariants();
        const variant  = variants.find(v => String(v.id) === String(variantId));
        if (variant) {
          const parts = (variant.public_title || '').split('/');
          if (parts.length > 1) garmentColor = parts[parts.length - 1].trim();
        }
      } catch(err) {}

      const locationInputs   = document.querySelectorAll('input[data-field-name="dropdown-1"]:checked');
      const imprintLocations = Array.from(locationInputs).map(i => i.value).join(', ');
      const productTitle     = document.querySelector('h1, .product__title')?.textContent?.trim() || '';
      const sizeBreakdown    = getSizeBreakdown();

      // Validate size breakdown if widget is present
      const sizeTotal = getSizeTotal();
      if (sizeTotal > 0 && sizeTotal !== qty) {
        alert('Size breakdown total (' + sizeTotal + ') must match your quantity (' + qty + '). Please adjust your sizes.');
        return;
      }

      // Loading state
      btn.disabled = true;
      btn.textContent = 'Processing...';
      btn.style.background = '#9ca3af';

      try {
        const response = await fetch(WORKER_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            variantId,
            quantity:        qty,
            calculatedPrice,
            garmentColor,
            numColors:       numColorsRaw,
            decoration:      getGloboValue(GLOBO_FIELD_LABELS.decorationType) || 'Screen Print',
            imprintLocations,
            colorMatch:      colorMatchChecked,
            colorMatchTotal,
            qtyRange:        variantLabel,
            productTitle,
            sizeBreakdown,
          }),
        });

        const data = await response.json();

        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          throw new Error(data.error || 'No checkout URL');
        }
      } catch(err) {
        console.error('Price Worker error:', err);
        btn.disabled = false;
        btn.textContent = 'Proceed to Checkout';
        btn.style.background = '#1B3A6B';
        alert('There was an issue processing your order. Please try again or contact us.');
      }
    });

    // Insert after the gpo-container (Globo options)
    const gpoContainer = document.querySelector('.gpo-container');
    if (gpoContainer) {
      gpoContainer.insertAdjacentElement('afterend', btn);
    } else {
      const buttonsContainer = document.querySelector('.product-form__buttons');
      if (buttonsContainer) buttonsContainer.appendChild(btn);
    }
  }


  // ── Init ──────────────────────────────────────────────────────────────────────
  function init() {
    if (!document.querySelector('.product-form, [data-product-form]')) return;
    blockGloboCartAdd(); // must run before any clicks
    injectQtyInput();
    ensurePriceDisplay();
    attachListeners();
    setTimeout(injectCheckoutButton, 1000); // after Globo renders
    setTimeout(injectSizeBreakdown, 1500);  // after checkout button
    setTimeout(recalculate, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 800);
  }

})();